import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteConfig } from '@/mocks/site';
import { reputationCards } from '@/mocks/faq';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteConfig.name}`,
    description: siteConfig.description,
    url: `${siteConfig.url}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: siteConfig.name,
      description: siteConfig.description,
      url: siteConfig.url,
      email: siteConfig.email,
      sameAs: [siteConfig.github],
    },
  };

  return (
    <>
      <ScrollToTop />
      <title>关于我们 - 1024社区</title>
      <meta name="description" content={`了解${siteConfig.name}的品牌故事、社区理念与发展历程。${siteConfig.description}`} />
      <meta name="keywords" content="1024社区介绍,程序员社区,技术交流平台,关于1024社区" />
      <link rel="canonical" href="https://1024.community/about" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="关于我们 - 1024社区" />
      <meta property="og:description" content={`了解${siteConfig.name}的品牌故事、社区理念与发展历程`} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section className="mb-16">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-6">
                关于 {siteConfig.name}
              </h1>
              <div>
                <p className="text-lg text-foreground-600 leading-relaxed mb-6">
                  <strong className="text-foreground-900">1024社区</strong>创立于2020年，是专为中文开发者打造的技术交流与资源分享平台。2的10次方是1024，这组数字在计算机科学中具有特殊意义，象征着我们对技术本质的追求与热爱。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  五年来，我们始终秉持"开放、共享、成长"的理念，汇聚了来自全球各地的百万开发者，共同构建了一个高质量的技术内容生态。从基础的编程入门教程到深度的架构设计探讨，从热门的开源项目推荐到前沿的AI技术解析，1024社区致力于为每一位开发者提供有价值的学习资源与交流空间。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  我们相信，技术的价值在于分享与传承。在1024社区，每一位开发者既是知识的学习者，也是经验的贡献者。通过文章分享、项目协作和技术讨论，我们共同推动着中文技术社区的发展与繁荣。
                </p>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-8">
                社区理念
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                  <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 mb-4">
                    <i className="ri-open-arm-line text-primary-600 text-xl"></i>
                  </span>
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">开放</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们坚持内容免费开放，让知识无障碍地传播。所有技术文章和资源均可自由访问和学习。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                  <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 mb-4">
                    <i className="ri-share-line text-accent-600 text-xl"></i>
                  </span>
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">共享</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    鼓励开发者分享经验与知识，通过开源项目和文章贡献，共同建设繁荣的技术生态。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                  <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-200 mb-4">
                    <i className="ri-seedling-line text-secondary-700 text-xl"></i>
                  </span>
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">成长</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    与百万开发者共同学习进步，在交流与实践中不断提升技术能力，实现个人与社区的同步成长。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-8">
                社区口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {reputationCards.map((card) => (
                  <div key={card.title} className="bg-background-50 rounded-lg p-6 border border-background-200/70 text-center">
                    <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-4">
                      <i className={`${card.icon} text-primary-600 text-2xl`}></i>
                    </span>
                    <h3 className="text-xl font-bold font-heading text-foreground-900 mb-2 whitespace-nowrap">{card.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{card.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-8">
                联系我们
              </h2>
              <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">联系方式</h3>
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                          <i className="ri-mail-line text-primary-600 text-lg"></i>
                        </span>
                        <div>
                          <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                          <a href={`mailto:${siteConfig.email}`} className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                            {siteConfig.email}
                          </a>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                          <i className="ri-github-fill text-primary-600 text-lg"></i>
                        </span>
                        <div>
                          <div className="text-sm font-medium text-foreground-900">GitHub</div>
                          <a href={siteConfig.github} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                            {siteConfig.github}
                          </a>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">加入社区</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                      欢迎所有热爱技术的开发者加入1024社区。无论是分享文章、参与开源项目还是提出建议，我们都期待你的参与。
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Link
                        to="/category/frontend"
                        className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                      >
                        浏览文章
                      </Link>
                      <a
                        href={`mailto:${siteConfig.email}`}
                        className="px-5 py-2.5 bg-background-50 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-md border border-background-300/60 transition-colors whitespace-nowrap"
                      >
                        发送邮件
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}